import {useMutation} from '@apollo/client';
import {useReviewContext} from '../../contexts/ReviewContext';
import {NoteFragment} from '../../schema-types/NoteFragment';
import {UpdateNote, UpdateNoteVariables} from '../../schema-types/UpdateNote';
import {cleanForCuro} from '../../utils/cleanString';
import {useSetIsJourneyToTrue} from '../../hooks/useSetIsJourneyToTrue';
import {
  GET_HANDOVER_NOTES_QUERY,
  UPDATE_NOTE_MUTATION,
} from '../../features/notes/notesQueries';

export const NoteMappings = {
  'Personal details': 'personal',
  'General details': 'personal',
  'Income & Expenditure': 'income',
  Protection: 'protection',
  'Beneficiary details': 'protection',
  'Retirement planning': 'retirement',
  Taxation: 'taxation',
  'Loans & Liabilities': 'loan',
  'Attitude to risk': 'riskattitude',
  'Saving & Investment': 'investment',
  'Trust assets': 'investment',
  'Other business': 'other',
  'Estate planning': 'estate',
};

export type HandoverNoteFields = keyof typeof NoteMappings;

export const getNoHardNotesRequired = (field: HandoverNoteFields) =>
  field === 'Personal details' ||
  field === 'Protection' ||
  field === 'Taxation' ||
  field === 'Other business' ||
  field === 'Estate planning' ||
  field === 'Attitude to risk';

export type FormikNote = {
  required: boolean;
  reviewed: boolean;
  displayName: HandoverNoteFields;
  name: HandoverNoteFields;
  id: string;
  body: string;
  markupBody: string | null;
  isVisible: boolean;
};

export type FormikNotesValues = Record<HandoverNoteFields, FormikNote>;

export const serverToEditingNotes = (serverNote: NoteFragment): FormikNote => {
  const noteName = serverNote.name as HandoverNoteFields;
  const displayName = serverNote.displayName as HandoverNoteFields;

  return {
    displayName,
    name: noteName,
    id: serverNote.id,
    reviewed: serverNote.reviewed === 'Reviewed but no requirements',
    body: serverNote.body || '',
    required: !!serverNote.required,
    isVisible: serverNote.isVisible || false,
    markupBody: serverNote.markupBody || serverNote.body || '',
  };
};

export function getReviewedValueFromField(note: FormikNote) {
  if (note.reviewed) {
    return 'Reviewed but no requirements';
  }
  if (note.markupBody) {
    return 'Requirements identified';
  }
  if (note.body) {
    return 'Requirements identified';
  }
  if (note.required && note.isVisible) {
    return 'Not reviewed';
  }
  return 'Not applicable';
}

export const notesOrder: HandoverNoteFields[] = [
  'Personal details',
  'General details',
  'Income & Expenditure',
  'Protection',
  'Beneficiary details',
  'Retirement planning',
  'Taxation',
  'Loans & Liabilities',
  'Attitude to risk',
  'Saving & Investment',
  'Trust assets',
  'Other business',
  'Estate planning',
];

export const doneWidth = 30;

export const getExtraText = (currentNotesType: HandoverNoteFields): string => {
  if (currentNotesType === 'Saving & Investment') {
    return " Don't forget to include Emergency fund in “Saving & Investment”";
  }
  if (currentNotesType === 'Retirement planning') {
    return " Don't forget to include Workplace pensions in “Retirement planning”";
  }
  if (currentNotesType === 'Income & Expenditure') {
    return " Don't forget to include Employment in the “Income & Expenditure” section";
  }
  return '';
};

export const getAllowedNoteLength = (currentNotesType: HandoverNoteFields) => {
  /**
   * loan, other < = 2000
   * personal, income, protection, retirement, taxation, atr, investment, estate <= 5000
   */
  if (
    currentNotesType === 'Loans & Liabilities' ||
    currentNotesType === 'Other business'
  ) {
    return 2000;
  }
  return 5000;
};

export const useOnUpdateNoteMutation = (refetch = true) => {
  const {reviewDetails} = useReviewContext();
  const {setIsJourneyToTrue} = useSetIsJourneyToTrue();
  const [onUpdateNotePure, {loading: onUpdateNoteLoading}] = useMutation<
    UpdateNote,
    UpdateNoteVariables
  >(UPDATE_NOTE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: refetch ? [GET_HANDOVER_NOTES_QUERY] : undefined,
  });
  const onUpdateNoteWithCleaning: typeof onUpdateNotePure = async (args) => {
    if (!reviewDetails) {
      return {};
    }
    if (!reviewDetails.isDraft) {
      // if not draft, do nothing
      return {};
    }

    if (!reviewDetails.isInJourney && reviewDetails.id) {
      await setIsJourneyToTrue({
        variables: {
          reviewId: reviewDetails.id,
        },
      });
    }

    return onUpdateNotePure({
      variables: {
        ...(args?.variables as UpdateNoteVariables),
        body: cleanForCuro(args?.variables?.body, true) as string,
      },
    });
  };
  return {
    _onUpdateNote: onUpdateNoteWithCleaning,
  };
};
